/**
 * Every word the Scenic sign-in page shows, in English, Hindi and Marathi —
 * the guest switches with EN / हिं / मरा. A page's own wording (organisation
 * name, tagline, footer) comes from the block's props; these are the rest.
 * `{name}` marks a value filled in at render time.
 */

export const LANGUAGES = [
    { code: 'en', label: 'EN' },
    { code: 'hi', label: 'हिं' },
    { code: 'mr', label: 'मरा' },
];

const STRINGS = {
    en: {
        dashValidTill: 'Valid till',
        dashTimeLeft: 'Time left',
        dashData: 'Data used',
        dashSpeed: 'Speed',
        dashUnlimited: 'Unlimited',
        dashDataOf: '{used} of {quota}',
        dashMbps: '{speed} Mbps',
        dashLeft: '{d}d {h}h {m}m',
        dashLeftHours: '{h}h {m}m',
        renewOrChange: 'Renew or change plan',
        yourPlan: 'Your plan',
        renewAdds: 'Renewing adds {duration} after {date}.',
        renew: 'Renew for ₹{amount}',
        expiredBanner: 'Your plan {plan} ended on {date}. Internet is paused. Choose a plan to continue.',
        tagline: 'Free public Wi-Fi',
        preview: 'Preview mode: not running on the hotspot. Use code 123456.',
        stepMobile: 'Mobile',
        stepCode: 'Code',
        stepPlan: 'Plan',
        stepOnline: 'Online',
        mobileTitle: 'Sign in with your mobile number',
        mobileLead: "We'll send you a one-time code.",
        sendBy: 'Send code by',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        whatsappHint: 'WhatsApp needs mobile data to receive the code. No data? Choose SMS.',
        mobileLabel: 'Mobile number',
        mobilePlaceholder: '10-digit number',
        agreePrefix: 'I agree to the',
        termsLink: 'terms of use',
        agreeSuffix: '',
        sendCode: 'Send code',
        codeTitle: 'Enter the code',
        codeLead: 'Sent to +91 {mobile} by {channel}.',
        preLoginNote: 'Internet is on for {minutes} min so WhatsApp can bring the code. Open WhatsApp, then come back here — don\'t close this page.',
        preLoginNoteApple: 'Internet is on for {minutes} min so WhatsApp can bring the code. It comes as a notification at the top of the screen — read it there and type it here. Don\'t leave this page.',
        codeLabel: '6-digit code',
        codePlaceholder: '------',
        verify: 'Verify and connect',
        resendIn: 'Resend code in {seconds}s',
        resend: 'Resend code',
        changeNumber: 'Change number',
        planTitle: 'Choose a plan',
        planLead: "Pay securely online. You're connected as soon as it's paid.",
        free: 'Free',
        nameLabel: 'Your name',
        namePlaceholder: 'Full name',
        emailLabel: 'Email (optional)',
        pay: 'Pay ₹{amount}',
        connectFree: 'Connect free',
        noPlans: 'No plans are available right now. Please ask at the help desk.',
        activeTitle: 'Your plan is active',
        activeLead: '{plan} · valid till {date}',
        connect: 'Connect',
        buyAnother: 'Buy another plan',
        checking: 'Checking your payment…',
        onlineTitle: "You're online",
        onlineLead: 'Your page will open in a few seconds.',
        continue: 'Continue',
        connecting: 'Connecting you…',
        previewOnline: 'Preview: on the hotspot the guest would now be online.',
        previewPay: 'Preview: no payment is taken here.',
        startOver: 'Start again',
        sessionFree: 'Free for {duration} per session',
        plansFrom: 'Plans from ₹{amount}',
        poweredBy: 'Powered by HotWiFi · Developed by {name}',
        errMobile: 'Enter a 10-digit mobile number.',
        errTerms: 'Please accept the terms of use.',
        errCode: 'Enter the 6-digit code.',
        errPreviewCode: 'In preview, the code is 123456.',
        errName: 'Please enter your name.',
        units: { minutes: ['minute', 'minutes'], hours: ['hour', 'hours'], days: ['day', 'days'], months: ['month', 'months'] },
    },
    hi: {
        dashValidTill: 'तक मान्य',
        dashTimeLeft: 'बचा समय',
        dashData: 'इस्तेमाल किया डेटा',
        dashSpeed: 'स्पीड',
        dashUnlimited: 'असीमित',
        dashDataOf: '{quota} में से {used}',
        dashMbps: '{speed} Mbps',
        dashLeft: '{d} दिन {h} घंटे {m} मिनट',
        dashLeftHours: '{h} घंटे {m} मिनट',
        renewOrChange: 'प्लान रिन्यू करें या बदलें',
        yourPlan: 'आपका प्लान',
        renewAdds: 'रिन्यू करने पर {date} के बाद {duration} जुड़ेंगे।',
        renew: '₹{amount} में रिन्यू करें',
        expiredBanner: 'आपका प्लान {plan} {date} को खत्म हो गया। इंटरनेट रुका है। जारी रखने के लिए प्लान चुनें।',
        tagline: 'मुफ़्त सार्वजनिक वाई-फ़ाई',
        preview: 'पूर्वावलोकन मोड: हॉटस्पॉट पर नहीं चल रहा। कोड 123456 डालें।',
        stepMobile: 'मोबाइल',
        stepCode: 'कोड',
        stepPlan: 'प्लान',
        stepOnline: 'ऑनलाइन',
        mobileTitle: 'अपने मोबाइल नंबर से साइन इन करें',
        mobileLead: 'हम आपको एक वन-टाइम कोड भेजेंगे।',
        sendBy: 'कोड कैसे भेजें',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        whatsappHint: 'WhatsApp पर कोड पाने के लिए मोबाइल डेटा चाहिए। डेटा नहीं है? SMS चुनें।',
        mobileLabel: 'मोबाइल नंबर',
        mobilePlaceholder: '10 अंकों का नंबर',
        agreePrefix: 'मैं',
        termsLink: 'उपयोग की शर्तों',
        agreeSuffix: 'से सहमत हूँ',
        sendCode: 'कोड भेजें',
        codeTitle: 'कोड डालें',
        codeLead: '+91 {mobile} पर {channel} से भेजा गया।',
        preLoginNote: 'WhatsApp पर कोड आ सके, इसलिए {minutes} मिनट के लिए इंटरनेट चालू है। WhatsApp खोलें, फिर यहाँ वापस आएँ — यह पेज बंद न करें।',
        preLoginNoteApple: 'WhatsApp पर कोड आ सके, इसलिए {minutes} मिनट के लिए इंटरनेट चालू है। कोड स्क्रीन के ऊपर नोटिफ़िकेशन में आएगा — वहीं देखकर यहाँ डालें। यह पेज न छोड़ें।',
        codeLabel: '6 अंकों का कोड',
        codePlaceholder: '------',
        verify: 'सत्यापित करें और कनेक्ट करें',
        resendIn: '{seconds} सेकंड में कोड फिर भेजें',
        resend: 'कोड फिर भेजें',
        changeNumber: 'नंबर बदलें',
        planTitle: 'प्लान चुनें',
        planLead: 'सुरक्षित ऑनलाइन भुगतान करें। भुगतान होते ही आप कनेक्ट हो जाएंगे।',
        free: 'मुफ़्त',
        nameLabel: 'आपका नाम',
        namePlaceholder: 'पूरा नाम',
        emailLabel: 'ईमेल (वैकल्पिक)',
        pay: '₹{amount} भुगतान करें',
        connectFree: 'मुफ़्त कनेक्ट करें',
        noPlans: 'अभी कोई प्लान उपलब्ध नहीं है। कृपया हेल्प डेस्क पर पूछें।',
        activeTitle: 'आपका प्लान चालू है',
        activeLead: '{plan} · {date} तक मान्य',
        connect: 'कनेक्ट करें',
        buyAnother: 'दूसरा प्लान लें',
        checking: 'आपका भुगतान जाँच रहे हैं…',
        onlineTitle: 'आप ऑनलाइन हैं',
        onlineLead: 'आपका पेज कुछ सेकंड में खुलेगा।',
        continue: 'आगे बढ़ें',
        connecting: 'आपको कनेक्ट कर रहे हैं…',
        previewOnline: 'पूर्वावलोकन: हॉटस्पॉट पर अतिथि अब ऑनलाइन होता।',
        previewPay: 'पूर्वावलोकन: यहाँ कोई भुगतान नहीं लिया जाता।',
        startOver: 'फिर से शुरू करें',
        sessionFree: 'हर सत्र के लिए {duration} मुफ़्त',
        plansFrom: '₹{amount} से प्लान',
        poweredBy: 'HotWiFi द्वारा संचालित · {name} द्वारा विकसित',
        errMobile: '10 अंकों का मोबाइल नंबर डालें।',
        errTerms: 'कृपया उपयोग की शर्तें स्वीकार करें।',
        errCode: '6 अंकों का कोड डालें।',
        errPreviewCode: 'पूर्वावलोकन में कोड 123456 है।',
        errName: 'कृपया अपना नाम डालें।',
        units: { minutes: ['मिनट', 'मिनट'], hours: ['घंटा', 'घंटे'], days: ['दिन', 'दिन'], months: ['महीना', 'महीने'] },
    },
    mr: {
        dashValidTill: 'पर्यंत वैध',
        dashTimeLeft: 'उरलेला वेळ',
        dashData: 'वापरलेला डेटा',
        dashSpeed: 'स्पीड',
        dashUnlimited: 'अमर्यादित',
        dashDataOf: '{quota} पैकी {used}',
        dashMbps: '{speed} Mbps',
        dashLeft: '{d} दिवस {h} तास {m} मिनिटे',
        dashLeftHours: '{h} तास {m} मिनिटे',
        renewOrChange: 'प्लॅन रिन्यू करा किंवा बदला',
        yourPlan: 'तुमचा प्लॅन',
        renewAdds: 'रिन्यू केल्यावर {date} नंतर {duration} जोडले जातील.',
        renew: '₹{amount} मध्ये रिन्यू करा',
        expiredBanner: 'तुमचा प्लॅन {plan} {date} रोजी संपला. इंटरनेट थांबले आहे. पुढे चालू ठेवण्यासाठी प्लॅन निवडा.',
        tagline: 'मोफत सार्वजनिक वाय-फाय',
        preview: 'पूर्वावलोकन मोड: हॉटस्पॉटवर चालू नाही. कोड 123456 टाका.',
        stepMobile: 'मोबाइल',
        stepCode: 'कोड',
        stepPlan: 'प्लॅन',
        stepOnline: 'ऑनलाइन',
        mobileTitle: 'तुमच्या मोबाइल नंबरने लॉग इन करा',
        mobileLead: 'आम्ही तुम्हाला एक वन-टाइम कोड पाठवू.',
        sendBy: 'कोड कसा पाठवायचा',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        whatsappHint: 'WhatsApp वर कोड मिळवण्यासाठी मोबाइल डेटा लागतो. डेटा नाही? SMS निवडा.',
        mobileLabel: 'मोबाइल नंबर',
        mobilePlaceholder: '10 अंकी नंबर',
        agreePrefix: 'मला',
        termsLink: 'वापराच्या अटी',
        agreeSuffix: 'मान्य आहेत',
        sendCode: 'कोड पाठवा',
        codeTitle: 'कोड टाका',
        codeLead: '+91 {mobile} वर {channel} ने पाठवला.',
        preLoginNote: 'WhatsApp वर कोड यावा म्हणून {minutes} मिनिटांसाठी इंटरनेट चालू आहे. WhatsApp उघडा आणि परत इथे या — हे पेज बंद करू नका.',
        preLoginNoteApple: 'WhatsApp वर कोड यावा म्हणून {minutes} मिनिटांसाठी इंटरनेट चालू आहे. कोड स्क्रीनच्या वर नोटिफिकेशनमध्ये येईल — तिथे बघून इथे टाका. हे पेज सोडू नका.',
        codeLabel: '6 अंकी कोड',
        codePlaceholder: '------',
        verify: 'पडताळा आणि कनेक्ट करा',
        resendIn: '{seconds} सेकंदांनी कोड पुन्हा पाठवा',
        resend: 'कोड पुन्हा पाठवा',
        changeNumber: 'नंबर बदला',
        planTitle: 'प्लॅन निवडा',
        planLead: 'सुरक्षित ऑनलाइन पेमेंट करा. पेमेंट होताच तुम्ही कनेक्ट व्हाल.',
        free: 'मोफत',
        nameLabel: 'तुमचे नाव',
        namePlaceholder: 'पूर्ण नाव',
        emailLabel: 'ईमेल (ऐच्छिक)',
        pay: '₹{amount} भरा',
        connectFree: 'मोफत कनेक्ट करा',
        noPlans: 'सध्या कोणताही प्लॅन उपलब्ध नाही. कृपया मदत कक्षात विचारा.',
        activeTitle: 'तुमचा प्लॅन चालू आहे',
        activeLead: '{plan} · {date} पर्यंत वैध',
        connect: 'कनेक्ट करा',
        buyAnother: 'दुसरा प्लॅन घ्या',
        checking: 'तुमचे पेमेंट तपासत आहोत…',
        onlineTitle: 'तुम्ही ऑनलाइन आहात',
        onlineLead: 'तुमचे पेज काही सेकंदांत उघडेल.',
        continue: 'पुढे जा',
        connecting: 'तुम्हाला कनेक्ट करत आहोत…',
        previewOnline: 'पूर्वावलोकन: हॉटस्पॉटवर पाहुणा आता ऑनलाइन झाला असता.',
        previewPay: 'पूर्वावलोकन: इथे कोणतेही पेमेंट घेतले जात नाही.',
        startOver: 'पुन्हा सुरू करा',
        sessionFree: 'प्रत्येक सत्रासाठी {duration} मोफत',
        plansFrom: '₹{amount} पासून प्लॅन',
        poweredBy: 'HotWiFi द्वारे संचालित · {name} ने विकसित केले',
        errMobile: '10 अंकी मोबाइल नंबर टाका.',
        errTerms: 'कृपया वापराच्या अटी मान्य करा.',
        errCode: '6 अंकी कोड टाका.',
        errPreviewCode: 'पूर्वावलोकनात कोड 123456 आहे.',
        errName: 'कृपया तुमचे नाव टाका.',
        units: { minutes: ['मिनिट', 'मिनिटे'], hours: ['तास', 'तास'], days: ['दिवस', 'दिवस'], months: ['महिना', 'महिने'] },
    },
};

export const languageOf = (code) => (STRINGS[code] ? code : 'en');

/** The phrase `key` in `lang`, with {name} filled from `vars`. */
export function t(lang, key, vars = {}) {
    const table = STRINGS[languageOf(lang)];
    const phrase = table[key] ?? STRINGS.en[key] ?? key;
    return String(phrase).replace(/\{(\w+)\}/g, (_, name) => (vars[name] ?? ''));
}

/** "1 hour", "30 दिवस" — a plan's validity, in `lang`. */
export function durationText(lang, validity) {
    if (!validity?.duration || !validity?.unit) return '';
    const units = STRINGS[languageOf(lang)].units[validity.unit] || STRINGS.en.units[validity.unit];
    if (!units) return `${validity.duration} ${validity.unit}`;
    return `${validity.duration} ${validity.duration === 1 ? units[0] : units[1]}`;
}
